/**
 * Right pane — per-layout edit forms (PR2) + slide management (PR3).
 *
 * Shape:
 *   - Deck-level meta at the top (title, theme, version, audience).
 *   - Per-slide management row (duplicate / delete with confirm — PR3).
 *   - Per-slide edit form, dispatched by detected layout (opener,
 *     two-col, paths, asks, generic) — plus a JSON tab for the
 *     fallback.
 *   - Autosave behaviour lives in ``useSlideAutosave``: debounced on
 *     change, flushed on blur, ETag/If-Match optimistic concurrency.
 */

import { LayoutDispatcher } from "../layouts/LayoutDispatcher";
import { useSlideAutosave, type SaveState } from "../useSlideAutosave";
import type { Deck } from "../types";
import { SlideActions } from "./SlideActions";

interface InspectorProps {
  deck: Deck;
  activeIdx: number;
  /** Called when the user duplicates the active slide (PR3). */
  onDuplicateSlide?(idx: number): void;
  /** Called when the user confirms deletion of the active slide (PR3). */
  onDeleteSlide?(idx: number): void;
  /** Disables slide-management buttons while a mutation is in flight. */
  slideManagementBusy?: boolean;
}

export function Inspector({
  deck,
  activeIdx,
  onDuplicateSlide,
  onDeleteSlide,
  slideManagementBusy = false,
}: InspectorProps) {
  const slide = deck.deck_json.slides[activeIdx];
  const showActions =
    slide && (onDuplicateSlide || onDeleteSlide) ? true : false;

  return (
    <aside style={wrapperStyle} data-testid="inspector">
      <DeckMetaSection deck={deck} />
      {slide ? (
        <SlideEditSection
          deck={deck}
          activeIdx={activeIdx}
          actions={
            showActions ? (
              <SlideActions
                slideNumber={activeIdx + 1}
                slideTitle={stripHtml(slide.titleHtml)}
                onDuplicate={() => onDuplicateSlide?.(activeIdx)}
                onDelete={() => onDeleteSlide?.(activeIdx)}
                busy={slideManagementBusy}
                deleteDisabled={deck.deck_json.slides.length <= 1}
              />
            ) : null
          }
        />
      ) : (
        <p style={{ color: "var(--gray-9)", padding: "1rem" }}>
          Pick a slide from the rail to inspect it.
        </p>
      )}
    </aside>
  );
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&[a-zA-Z]+;/g, " ")
    .trim();
}

function DeckMetaSection({ deck }: { deck: Deck }) {
  return (
    <section style={sectionStyle} data-testid="inspector-deck-meta">
      <h3 style={sectionTitleStyle}>Deck</h3>
      <KV label="Title" value={deck.title} />
      <KV label="Slug" value={deck.slug} mono />
      <KV label="Theme" value={deck.theme} mono />
      <KV label="Version" value={String(deck.version)} mono />
      <KV label="Slides" value={String(deck.deck_json.slides.length)} mono />
      <KV label="Audience" value={deck.audience_class ?? "—"} mono />
      <KV label="Cache tag" value={deck.deck_json.cacheTag} mono />
      {deck.audience_class === "customer_facing" && <IpSafetyBanner />}
    </section>
  );
}

function SlideEditSection({
  deck,
  activeIdx,
  actions,
}: {
  deck: Deck;
  activeIdx: number;
  actions: React.ReactNode;
}) {
  const { draft, setField, flush, saveState } = useSlideAutosave(
    deck,
    activeIdx,
  );

  return (
    <section
      style={sectionStyle}
      data-testid={`inspector-slide-${activeIdx}`}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: "0.5rem",
        }}
      >
        <h3 style={sectionTitleStyle}>Slide {activeIdx + 1}</h3>
        <SaveStatePill state={saveState} />
      </header>
      {actions}
      <LayoutDispatcher draft={draft} setField={setField} flush={flush} />
    </section>
  );
}

function SaveStatePill({ state }: { state: SaveState }) {
  switch (state.status) {
    case "idle":
      return null;
    case "saving":
      return (
        <span style={pillStyle("var(--gray-9)", "var(--gray-3)")} data-testid="save-state-saving">
          Saving…
        </span>
      );
    case "saved":
      return (
        <span style={pillStyle("var(--green-11)", "var(--green-3)")} data-testid="save-state-saved">
          Saved
        </span>
      );
    case "conflict":
      return (
        <span
          style={pillStyle("var(--amber-12)", "var(--amber-3)")}
          title={state.message}
          data-testid="save-state-conflict"
        >
          Conflict
        </span>
      );
    case "error":
      return (
        <span
          style={pillStyle("var(--red-11)", "var(--red-3)")}
          title={state.message}
          data-testid="save-state-error"
        >
          Save failed
        </span>
      );
  }
}

function IpSafetyBanner() {
  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.5rem 0.75rem",
        border: "1px solid var(--amber-7)",
        background: "var(--amber-3)",
        color: "var(--amber-12)",
        borderRadius: 4,
        fontSize: "0.85em",
      }}
      data-testid="ip-safety-banner"
    >
      <strong>Customer-facing audience.</strong> The IP-safety gate (#13130)
      will inspect every slide on render. Violations are logged in warn mode
      today; once enforcement is on, render will refuse decks that mix
      restricted sources with this audience class.
    </div>
  );
}

function KV({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div style={kvRowStyle}>
      <span style={kvLabelStyle}>{label}</span>
      <span
        style={{
          ...kvValueStyle,
          fontFamily: mono ? "monospace" : "inherit",
        }}
      >
        {value}
      </span>
    </div>
  );
}

const wrapperStyle: React.CSSProperties = {
  width: 380,
  flex: "0 0 380px",
  borderLeft: "1px solid var(--gray-5)",
  background: "var(--gray-1)",
  overflowY: "auto",
};

const sectionStyle: React.CSSProperties = {
  padding: "0.75rem 1rem",
  borderBottom: "1px solid var(--gray-5)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "0.85em",
  fontWeight: 600,
  color: "var(--gray-11)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const kvRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "0.5rem",
  padding: "0.2rem 0",
  fontSize: "0.9em",
};

const kvLabelStyle: React.CSSProperties = {
  flex: "0 0 110px",
  color: "var(--gray-10)",
  fontSize: "0.8em",
};

const kvValueStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

function pillStyle(
  color: string,
  background: string,
): React.CSSProperties {
  return {
    padding: "0.15rem 0.5rem",
    background,
    color,
    borderRadius: 999,
    fontSize: "0.7em",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontWeight: 600,
  };
}
